// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { onMounted, onUnmounted, nextTick } from 'vue';

// 🔵 Intent: Task 006 - initialise Bootstrap 5 Tooltips on every
// `[data-bs-toggle="tooltip"]` element inside the calling component. Each
// component that owns tooltip-bearing markup should call this composable in
// its <script setup> so tooltips initialise when the component actually
// mounts. Failures are silently swallowed: tooltips are progressive
// enhancement and must never block the UI on init errors.
export function useBootstrapTooltips() {
  let instances = [];

  async function initTooltips() {
    try {
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
      }
      const mod = await import('bootstrap');
      const Tooltip = mod.Tooltip;
      if (!Tooltip || typeof Tooltip.getOrCreateInstance !== 'function') {
        return;
      }
      const elements = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      instances = Array.from(elements).map((el) =>
        Tooltip.getOrCreateInstance(el),
      );
    } catch {
      // Tooltips are non-essential; silently degrade.
    }
  }

  onMounted(async () => {
    await nextTick();
    initTooltips();
  });

  onUnmounted(() => {
    instances.forEach((t) => {
      try {
        if (t && typeof t.dispose === 'function') t.dispose();
      } catch {
        // ignore
      }
    });
    instances = [];
  });
}
